import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ChildConfig, ChildSleeping } from '@/lib/occupancy-utils';
import type { GuestFormData } from '@/components/public/checkout-guest-form';
import { INITIAL_GUEST_FORM } from '@/components/public/checkout-guest-form';
import { createExpiringSessionStorage, ONE_DAY_MS } from './expiring-storage';

export interface RoomOccupancy {
  adults: number;
  children: number;
  childConfigs: ChildConfig[];
  bedSetup: 'double' | 'twin';
}

export const DEFAULT_OCCUPANCY: RoomOccupancy = {
  adults: 2,
  children: 0,
  childConfigs: [],
  bedSetup: 'double',
};

interface CheckoutDraftStore {
  formData: GuestFormData;
  phoneCountry: string;
  singleOccupancy: RoomOccupancy;
  occupanciesBySlug: Record<string, RoomOccupancy>;
  selectedPlanId: string | null;

  setFormData: (formData: GuestFormData) => void;
  setPhoneCountry: (code: string) => void;
  updateSingleOccupancy: (patch: Partial<RoomOccupancy>) => void;
  setSingleChildren: (value: number) => void;
  updateSingleChildAge: (index: number, age: number | null) => void;
  updateSingleChildSleeping: (index: number, sleeping: ChildSleeping) => void;
  setRoomOccupancy: (slug: string, occ: RoomOccupancy) => void;
  updateRoomOccupancy: (slug: string, patch: Partial<RoomOccupancy>) => void;
  setRoomChildren: (slug: string, value: number) => void;
  updateRoomChildAge: (slug: string, index: number, age: number | null) => void;
  updateRoomChildSleeping: (slug: string, index: number, sleeping: ChildSleeping) => void;
  setSelectedPlanId: (id: string | null) => void;
  clearDraft: () => void;
}

const INITIAL_STATE = {
  formData: INITIAL_GUEST_FORM,
  phoneCountry: 'RO',
  singleOccupancy: { ...DEFAULT_OCCUPANCY },
  occupanciesBySlug: {} as Record<string, RoomOccupancy>,
  selectedPlanId: null as string | null,
};

function resizeChildConfigs(prev: ChildConfig[], target: number): ChildConfig[] {
  if (target > prev.length) {
    return [
      ...prev,
      ...Array.from({ length: target - prev.length }, () => ({ age: null as number | null })),
    ];
  }
  return prev.slice(0, target);
}

export const useCheckoutDraftStore = create<CheckoutDraftStore>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      setFormData: (formData) => set({ formData }),
      setPhoneCountry: (phoneCountry) => set({ phoneCountry }),

      updateSingleOccupancy: (patch) =>
        set((s) => ({ singleOccupancy: { ...s.singleOccupancy, ...patch } })),

      setSingleChildren: (value) =>
        set((s) => ({
          singleOccupancy: {
            ...s.singleOccupancy,
            children: value,
            childConfigs: resizeChildConfigs(s.singleOccupancy.childConfigs, value),
          },
        })),

      updateSingleChildAge: (index, age) =>
        set((s) => {
          const others = s.singleOccupancy.childConfigs.filter((_, i) => i !== index);
          const parentBedTaken = others.some((c) => c.sleeping === 'parent-bed');
          const defaultSleeping: ChildSleeping = parentBedTaken ? 'baby-cot' : 'parent-bed';
          return {
            singleOccupancy: {
              ...s.singleOccupancy,
              childConfigs: s.singleOccupancy.childConfigs.map((c, i) => {
                if (i !== index) return c;
                if (age !== null && age <= 5) {
                  return { age, sleeping: c.sleeping || defaultSleeping };
                }
                // Age 6+ is forced onto an extra bed by occupancy rules — the
                // form doesn't even render a sleeping picker for them. Persist
                // 'extra-bed' explicitly so downstream readers (PYN comment,
                // wire payload) don't fall back to nonsense defaults.
                if (age !== null && age >= 6) {
                  return { age, sleeping: 'extra-bed' };
                }
                return { age };
              }),
            },
          };
        }),

      updateSingleChildSleeping: (index, sleeping) =>
        set((s) => ({
          singleOccupancy: {
            ...s.singleOccupancy,
            childConfigs: s.singleOccupancy.childConfigs.map((c, i) =>
              i === index ? { ...c, sleeping } : c,
            ),
          },
        })),

      setRoomOccupancy: (slug, occ) =>
        set((s) => ({
          occupanciesBySlug: { ...s.occupanciesBySlug, [slug]: occ },
        })),

      updateRoomOccupancy: (slug, patch) =>
        set((s) => {
          const current = s.occupanciesBySlug[slug] || { ...DEFAULT_OCCUPANCY };
          return {
            occupanciesBySlug: {
              ...s.occupanciesBySlug,
              [slug]: { ...current, ...patch },
            },
          };
        }),

      setRoomChildren: (slug, value) =>
        set((s) => {
          const current = s.occupanciesBySlug[slug] || { ...DEFAULT_OCCUPANCY };
          return {
            occupanciesBySlug: {
              ...s.occupanciesBySlug,
              [slug]: {
                ...current,
                children: value,
                childConfigs: resizeChildConfigs(current.childConfigs, value),
              },
            },
          };
        }),

      updateRoomChildAge: (slug, index, age) =>
        set((s) => {
          const current = s.occupanciesBySlug[slug] || { ...DEFAULT_OCCUPANCY };
          const others = current.childConfigs.filter((_, i) => i !== index);
          const parentBedTaken = others.some((c) => c.sleeping === 'parent-bed');
          const defaultSleeping: ChildSleeping = parentBedTaken ? 'baby-cot' : 'parent-bed';
          return {
            occupanciesBySlug: {
              ...s.occupanciesBySlug,
              [slug]: {
                ...current,
                childConfigs: current.childConfigs.map((c, i) => {
                  if (i !== index) return c;
                  if (age !== null && age <= 5) {
                    return { age, sleeping: c.sleeping || defaultSleeping };
                  }
                  if (age !== null && age >= 6) {
                    return { age, sleeping: 'extra-bed' };
                  }
                  return { age };
                }),
              },
            },
          };
        }),

      updateRoomChildSleeping: (slug, index, sleeping) =>
        set((s) => {
          const current = s.occupanciesBySlug[slug] || { ...DEFAULT_OCCUPANCY };
          return {
            occupanciesBySlug: {
              ...s.occupanciesBySlug,
              [slug]: {
                ...current,
                childConfigs: current.childConfigs.map((c, i) =>
                  i === index ? { ...c, sleeping } : c,
                ),
              },
            },
          };
        }),

      setSelectedPlanId: (selectedPlanId) => set({ selectedPlanId }),

      clearDraft: () => set({ ...INITIAL_STATE, occupanciesBySlug: {} }),
    }),
    {
      name: 'checkout-draft',
      storage: createJSONStorage(() => createExpiringSessionStorage(ONE_DAY_MS)),
    },
  ),
);
